package redmcrosco.alex.farmatch.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import redmcrosco.alex.farmatch.voice.VoiceCommandParser
import redmcrosco.alex.farmatch.voice.VoiceIntent
import redmcrosco.alex.farmatch.voice.rememberVoiceRecognizer

/**
 * Pulsante di ricerca a voce, riusabile ovunque nell'app. Come [VoiceFillButton] in
 * `DisponibilitaScreen.kt`: riconoscimento vocale locale (nessun costo), parser locale,
 * nessuna chiamata inviata da sola — passa solo l'intento riconosciuto al chiamante, che decide
 * come applicarlo ai propri filtri di ricerca esistenti.
 *
 * Il risultato ("Ho sentito: ...") resta scritto sotto il pulsante finché non si riprova (non
 * uno Snackbar che sparisce da solo), e la pagina scorre automaticamente fino a qui quando il
 * microfono ha finito di ascoltare, così il risultato è sempre visibile.
 *
 * Non visibile se il riconoscimento vocale non è disponibile sulla piattaforma corrente
 * (Desktop, Wasm per ora): la ricerca via tastiera/filtri resta l'unico modo, come oggi.
 *
 * @param onIntent applica l'intento riconosciuto ai filtri del chiamante e restituisce il
 *   messaggio da mostrare sotto il pulsante (es. "Ho cercato in base a quello che ho capito").
 */
@OptIn(androidx.compose.foundation.ExperimentalFoundationApi::class)
@Composable
fun VoiceSearchButton(
    hint: String,
    onIntent: (rawText: String, intent: VoiceIntent) -> String,
    onError: (String) -> Unit
) {
    val voiceRecognizer = rememberVoiceRecognizer()
    if (!voiceRecognizer.isAvailable()) return
    var isListening by remember { mutableStateOf(false) }
    var voiceFeedback by remember { mutableStateOf<String?>(null) }
    val bringIntoViewRequester = remember { BringIntoViewRequester() }
    val scope = rememberCoroutineScope()

    Column(modifier = Modifier.bringIntoViewRequester(bringIntoViewRequester)) {
        OutlinedButton(
            onClick = {
                isListening = true
                voiceRecognizer.startListening(
                    onResult = { text ->
                        isListening = false
                        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                        val intent = VoiceCommandParser.parse(text, today)
                        println("--- [VOICE] Testo: \"$text\" -> Intento: $intent ---")
                        voiceFeedback = onIntent(text, intent)
                        scope.launch { bringIntoViewRequester.bringIntoView() }
                    },
                    onError = { msg ->
                        isListening = false
                        voiceFeedback = msg
                        onError(msg)
                        scope.launch { bringIntoViewRequester.bringIntoView() }
                    }
                )
            },
            enabled = !isListening,
            modifier = Modifier.fillMaxWidth()
        ) {
            Icon(AppIcons.Mic, contentDescription = null, tint = if (isListening) Color.Red else Color.Black)
            Spacer(Modifier.width(6.dp))
            Text(text = if (isListening) "In ascolto..." else hint, fontSize = 11.sp)
        }
        if (voiceFeedback != null) {
            Text(
                text = voiceFeedback!!,
                style = MaterialTheme.typography.labelSmall,
                color = Color.Gray,
                modifier = Modifier.padding(top = 4.dp)
            )
        }
    }
}
